import React, { useState } from 'react';
import { CheckIcon, ChevronDownIcon, InfinityIcon, GiftIcon } from 'lucide-react';
import { QuantityStepper, money } from './QuantityStepper';
import { GoldMedal } from './LifetimeSeal';
import { PrescriptionBadge } from './PrescriptionBadge';
import { offers, Offer, checkoutUrl } from '../data/product';
import { Link } from 'react-router-dom';
import { CompanyPack } from './CompanyPack';
import { IncludedGear } from './IncludedGear';
import { FreeUnitModal } from './FreeUnitModal';
import { PaymentLogos } from './PaymentLogos';
import { UspsBadge } from './UspsBadge';
import { SecureCheckoutNote } from './SecureCheckoutNote';
import { trackCustom, packEvents } from '../utils/tracking';
import { LaunchOfferPanel } from './LaunchOfferPanel';
import { PromoCountdown } from './PromoCountdown';
import { PRODUCT_NAME, warrantyCopy, pricing } from '../data/promotion';
import { useActiveSale } from '../hooks/useActiveSale';
interface OfferSectionProps {
  selectedOffer: Offer['id'];
  onSelectOffer: (id: Offer['id']) => void;
}
const packMath: Record<Offer['id'], {
  price: number;
  units: number;
  compareAt?: number;
  save?: number;
}> = {
  single: {
    price: 49.95,
    units: 1,
    compareAt: 124.95,
    save: 75
  },
  trio: {
    price: 99.9,
    units: 3,
    compareAt: 374.85,
    save: 274.95
  },
  crew: {
    price: 179.95,
    units: 6,
    compareAt: 749.7,
    save: 569.75
  }
};
export function OfferSection({
  selectedOffer,
  onSelectOffer
}: OfferSectionProps) {
  const [quantities, setQuantities] = useState<Record<Offer['id'], number>>({
    single: 1,
    trio: 1,
    crew: 1
  });
  const [freeUnitOpen, setFreeUnitOpen] = useState(false);
  const [freeUnitDeclined, setFreeUnitDeclined] = useState(false);
  const sale = useActiveSale();
  const handleQuantity = (id: Offer['id'], next: number) => {
    // Two singles cost exactly what the 3-pack costs, so surface the free unit
    // instead of letting anyone overpay for the same money.
    if (id === 'single' && next >= 2 && !freeUnitDeclined) {
      setFreeUnitOpen(true);
      return;
    }
    setQuantities(prev => ({
      ...prev,
      [id]: next
    }));
  };
  const acceptFreeUnit = () => {
    setFreeUnitOpen(false);
    setQuantities(prev => ({
      ...prev,
      single: 1,
      trio: 1
    }));
    onSelectOffer('trio');
    // Land on the 3-pack's buy button so the next action is right under the eye.
    const target = document.getElementById('pack-trio-cta');
    if (target) target.scrollIntoView({
      behavior: 'smooth',
      block: 'end'
    });
  };
  const declineFreeUnit = () => {
    setFreeUnitOpen(false);
    setFreeUnitDeclined(true);
    setQuantities(prev => ({
      ...prev,
      single: 2
    }));
  };
  return <section id="offer" className="border-b border-white/10 bg-ink-900" data-id="element-559" data-mp="316dgqeiko5qs">
      <div className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6 lg:px-8 lg:py-20" data-id="element-560" data-mp="363vo40gxycwo">
        <h2 className="grit-headline text-[clamp(2.5rem,9vw,5rem)] font-extrabold uppercase text-offwhite" data-id="element-561" data-mp="2s4nq3awilau2">
          Choose your <span className="text-grit-500" data-id="element-562" data-mp="2x408njgcrvqu">loadout.</span>
        </h2>
        <p className="mt-4 text-lg font-medium text-steel-300 sm:text-xl" data-id="element-563" data-mp="2dhxbj7ts5lqj">
          {PRODUCT_NAME}. Every X1 ships as the complete system.
        </p>

        <LaunchOfferPanel data-id="element-564" data-mp="28k5j8cp83xlt" />

        <div className="mt-6 border-2 gold-edge bg-[#1a1408] p-5 sm:p-6" data-id="element-565" data-mp="28kykiy5exuwk">
          <p className="flex flex-wrap items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.24em] text-[#f2c94c]" data-id="element-566" data-mp="2mk6l9vh85b2v">
            <InfinityIcon className="h-4 w-4" aria-hidden="true" data-id="element-567" data-mp="2ijec128cr66l" />
            {warrantyCopy.value} · Free with every multi-pack
          </p>
          <p className="grit-headline mt-2 text-[clamp(1.75rem,5vw,3rem)] font-extrabold uppercase gold-text" data-id="element-568" data-mp="2riht60nylbig">
            {warrantyCopy.title}
          </p>
          <p className="mt-2 max-w-3xl text-[15px] font-semibold text-steel-200 sm:text-lg" data-id="element-569" data-mp="368fapxrdv2yo">
            {warrantyCopy.tagline} Break it, crack it, scratch a lens and we send a replacement. You never buy work
            glasses or a headlamp again. Included on Buy 2 Get 1 Free, Buy 4 Get 2 Free and the Crew Pack; single
            systems carry 1-year coverage.{' '}
            <Link to="/legal/guarantee" className="text-[13px] font-medium text-steel-400 underline" data-id="element-570" data-mp="18iik775jbj8r">
              Terms apply.
            </Link>
          </p>
        </div>

        <ul className="mt-10 flex flex-col gap-6 lg:grid lg:grid-cols-3 lg:items-start" data-id="element-571" data-mp="1i9wff2qgdww3">
          {offers.map(offer => {
          const isSelected = selectedOffer === offer.id;
          const qty = quantities[offer.id];
          const math = packMath[offer.id];
          const orderTotal = math.price * qty;
          const orderUnits = math.units * qty;
          return <li key={offer.id} id={`pack-${offer.id}`} className={`h-full scroll-mt-28 ${offer.featured ? '-order-1 lg:order-none' : ''}`} data-id="element-572" data-mp="ykkwckrl7ogo">
                <article className={`relative flex h-full flex-col border bg-ink-950 p-6 transition-colors duration-150 ease-grit lg:p-7 ${isSelected ? 'border-grit-500' : 'border-white/12 hover:border-white/30'} ${offer.featured ? 'lg:-mt-4 lg:pb-9' : ''}`} data-id="element-573" data-mp="29yzm0p5tbjbw">
                  <p className="-mx-6 -mt-6 mb-5 flex items-center justify-between gap-3 bg-grit-500 px-6 py-2.5 text-ink-950 lg:-mx-7 lg:-mt-7 lg:px-7" data-id="element-574" data-mp="2k614wavbtmf1">
                    <span className="font-display text-2xl font-extrabold uppercase leading-none" data-id="element-575" data-mp="2bcfokn3ijx3i">60% off</span>
                    <span className="text-right text-[10px] font-extrabold uppercase leading-tight tracking-[0.16em]" data-id="element-576" data-mp="26dcj51jmfov0">
                      {sale.name}
                      {math.units > 1 && <span className="block" data-id="element-577" data-mp="1wdu2ov2x6l7p">+ free units</span>}
                    </span>
                  </p>

                  {offer.image ? <>
                      {/* The pack artwork already carries the badge, the offer, the unit count and the per-X1 price. */}
                      <h3 className="sr-only" data-id="element-578" data-mp="3mq9rs3h8fa1t">
                        {[offer.eyebrow, offer.kicker, offer.name].filter(Boolean).join(' · ')}
                      </h3>
                      <img src={offer.image} alt={offer.imageAlt ?? offer.name} loading="lazy" width={1122} height={1402} className="h-auto w-full border border-white/10" data-id="element-579" data-mp="1k2b45o14hqhf" />
                    </> : <>
                      {offer.eyebrow && <p className={`mb-3 inline-flex self-start px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] ${offer.featured ? 'bg-grit-500 text-ink-950' : 'border border-grit-500/60 text-grit-500'}`} data-id="element-580" data-mp="27snj3iuscnhi">
                          {offer.eyebrow}
                        </p>}
                      {offer.kicker && <p className="text-xs font-bold uppercase tracking-[0.22em] text-grit-500" data-id="element-581" data-mp="ewl252utw2cy">{offer.kicker}</p>}
                      <h3 className={`mt-1 font-display font-extrabold uppercase leading-none text-offwhite ${offer.featured ? 'text-4xl sm:text-5xl' : 'text-3xl sm:text-4xl'}`} data-id="element-582" data-mp="auhoc7kosloi">
                        {offer.name}
                      </h3>
                      <p className="mt-2 text-xs font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-583" data-mp="3s9er1z9hesoa">{offer.units}</p>
                    </>}

                  <div className="mt-6 flex flex-wrap items-end gap-x-3 gap-y-1" data-id="element-584" data-mp="5xr89k8vtq33">
                    <span className={`font-display font-extrabold leading-none text-offwhite ${offer.featured ? 'text-6xl sm:text-7xl' : 'text-5xl'}`} data-id="element-585" data-mp="14evc7dkf7q6">
                      {money(orderTotal)}
                    </span>
                    {math.compareAt && <span className="pb-1 text-base text-steel-500 line-through" data-id="element-586" data-mp="2o1yghlo222cb">
                        {money(math.compareAt * qty)}
                      </span>}
                  </div>

                  {math.save && <p className="mt-2 font-display text-xl font-bold uppercase tracking-wide text-grit-500" data-id="element-587" data-mp="2y15vseeco722">
                      You save {money(math.save * qty)}
                    </p>}
                  {offer.perUnit && !offer.image && <p className="mt-1 text-sm font-semibold uppercase tracking-wide text-steel-300" data-id="element-588" data-mp="3brstc38djqwk">
                      {offer.perUnit}
                    </p>}
                  {qty > 1 && <p className="mt-1 text-sm font-semibold uppercase tracking-wide text-steel-300" data-id="element-589" data-mp="3lqhzwzutloxi">
                      {qty} packs · {orderUnits} X1 total
                    </p>}

                  {offer.highlight && !offer.image && <p className="mt-4 bg-grit-500 px-4 py-3 font-display text-2xl font-extrabold uppercase leading-none tracking-wide text-ink-950 sm:text-3xl" data-id="element-590" data-mp="1e1m0cpkao7k2">
                      {offer.highlight}
                    </p>}
                  {offer.runtime && <p className="mt-3 font-display text-lg font-bold uppercase leading-tight tracking-wide text-offwhite" data-id="element-591" data-mp="106a58h288f2m">
                      {offer.runtime}
                    </p>}

                  <p className="mt-4 text-[13px] font-semibold uppercase tracking-wide text-steel-300" data-id="element-592" data-mp="1suxsgi6jdeit">
                    {money(pricing.separateValue)} total value per X1
                  </p>

                  <div className="mt-5 border-t border-white/10 pt-5" data-id="element-593" data-mp="1j0xhw0shjh2p">
                    {/* Everyday items are pictured in the artwork's icon strip, so the list lives one tap away. */}
                    <details className="group" data-id="element-594" data-mp="2bqe7kqoq0jv8">
                      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-[11px] font-bold uppercase tracking-[0.2em] text-steel-300 transition-colors duration-150 ease-grit hover:text-offwhite [&::-webkit-details-marker]:hidden" data-id="element-595" data-mp="3vgauif3zhh18">
                        Full list of what&apos;s in the box
                        <ChevronDownIcon className="h-4 w-4 shrink-0 transition-transform duration-150 ease-grit group-open:rotate-180" aria-hidden="true" data-id="element-596" data-mp="49q50ztkuh6b" />
                      </summary>
                      <ul className="mt-3 grid gap-2 text-[14px] font-medium text-steel-200" data-id="element-597" data-mp="s0n9xsmyge82">
                        {offer.includes.filter(line => !/1-year|warranty|mystery gift/i.test(line)).map(line => <li key={line} className="flex items-start gap-2.5" data-id="element-598" data-mp="10yfouvorljux">
                              <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-599" data-mp="x11gf68wrg3z" />
                              {line}
                            </li>)}
                      </ul>
                    </details>
                    <ul className="mt-4 grid gap-2 text-[15px] font-medium text-offwhite" data-id="element-600" data-mp="16lrcjy7f6wdf">
                      {offer.includes.filter(line => /1-year|warranty|mystery gift/i.test(line)).map(line => {
                    if (/1-year/i.test(line)) {
                      return <li key={line} className="mt-1 grid gap-2" data-id="element-601" data-mp="11orheic3ze13">
                              <span className="flex items-start gap-2.5" data-id="element-602" data-mp="nwl2t0oltf3r">
                                <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-603" data-mp="nui13je39xj7" />
                                {line}
                              </span>
                              <button type="button" onClick={() => {
                          onSelectOffer('trio');
                          document.getElementById('pack-trio-cta')?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'end'
                          });
                        }} className="group flex items-center gap-3 border gold-edge bg-[#1a1408] p-3.5 text-left transition-colors duration-150 ease-grit hover:bg-[#241b0b] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f2c94c]" data-id="element-604" data-mp="e08942t27190">
                                <InfinityIcon className="h-7 w-7 shrink-0 text-[#f2c94c]" aria-hidden="true" data-id="element-605" data-mp="2zhtyytq5c9pd" />
                                <span data-id="element-606" data-mp="2plh4htms1rtk">
                                  <span className="block font-display text-base font-bold uppercase leading-tight text-[#ffe79a]" data-id="element-607" data-mp="2pkfjoxfylqyn">
                                    Want it covered for life?
                                  </span>
                                  <span className="mt-0.5 block text-[13px] font-semibold text-steel-300" data-id="element-608" data-mp="1e8eph30btabi">
                                    For just $49.95 more: 2 extra X1 plus the Lifetime Replacement Warranty on all
                                    3. Upgrade to Buy 2, Get 1 Free →
                                  </span>
                                </span>
                              </button>
                            </li>;
                    }
                    if (/warranty/i.test(line)) {
                      return <li key={line} className="mt-1" data-id="element-609" data-mp="1rqek3iyi58ki">
                              <Link to="/legal/guarantee" className="group flex items-center gap-4 border gold-edge bg-[#1a1408] p-4 transition-[background-color,box-shadow] duration-150 ease-grit hover:bg-[#241b0b] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f2c94c]" data-id="element-610" data-mp="1wqiy92bxtr7k">
                                <GoldMedal size={86} data-id="element-611" data-mp="1xsznf3rvz9ow" />
                                <p className="font-display text-lg font-bold uppercase leading-tight tracking-wide text-[#ffe79a]" data-id="element-612" data-mp="1xronxg5h4o3l">
                                  {math.units > 1 ? `All ${math.units} covered for life.` : 'Covered for life.'}
                                  <span className="mt-0.5 block text-[13px] font-semibold normal-case tracking-normal text-steel-300" data-id="element-613" data-mp="21v32s3hoeyfg">
                                    {warrantyCopy.title}. {warrantyCopy.tagline} {warrantyCopy.value},{' '}
                                    {warrantyCopy.status.toLowerCase()}.
                                  </span>
                                </p>
                              </Link>
                            </li>;
                    }
                    if (/mystery gift/i.test(line)) {
                      return <li key={line} className="mt-1 border border-grit-500/60 bg-grit-500/10 p-3.5 font-display text-lg font-bold uppercase leading-tight tracking-wide text-grit-500" data-id="element-614" data-mp="qg7oiimxyyzf">
                              <span className="flex items-center gap-2" data-id="element-615" data-mp="2t2bp9e87ygd3">
                                <GiftIcon className="h-4 w-4" aria-hidden="true" data-id="element-616" data-mp="2t08n5vtskws0" />
                                Free mystery gift
                              </span>
                              <span className="mt-1 block text-[13px] font-semibold normal-case tracking-normal text-steel-300" data-id="element-617" data-mp="3lsbvbgeghzhj">
                                There&apos;s something else in the box. No clues. No extra charge. While supplies
                                last.
                              </span>
                            </li>;
                    }
                    return <li key={line} className="flex items-start gap-2.5" data-id="element-618" data-mp="20fqxicnu0290">
                            <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-619" data-mp="25ebwy9v9ehwe" />
                            {line}
                          </li>;
                  })}
                    </ul>
                    {offer.splitNote && <div className="mt-4 border-l-2 border-grit-500 bg-white/[0.03] px-4 py-3" data-id="element-620" data-mp="1ch1bhd61ql11">
                        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-grit-500" data-id="element-621" data-mp="17ig9b878hr9y">
                          {offer.splitNote.title}
                        </p>
                        <p className="mt-1 text-[14px] font-medium text-steel-200" data-id="element-622" data-mp="xk0kxczlcw45">{offer.splitNote.body}</p>
                        <p className="mt-1 font-display text-base font-bold uppercase tracking-wide text-offwhite" data-id="element-623" data-mp="tk9mqo7qjdpl">
                          {offer.splitNote.each}
                        </p>
                      </div>}
                    <PrescriptionBadge className="mt-3" compact data-id="element-624" data-mp="2nv57sq03wl99" />
                    <ul className="mt-4 grid gap-1 border-t border-white/5 pt-4 text-sm text-steel-400" data-id="element-625" data-mp="2iyxycn8i4rmk">
                      {offer.supporting.map(line => <li key={line} data-id="element-626" data-mp="2vgh24ouwrmw6">{line}</li>)}
                    </ul>
                  </div>

                  <div className="mt-auto pt-7" data-id="element-627" data-mp="308s6q50lfwv2">
                    <QuantityStepper value={qty} onChange={next => handleQuantity(offer.id, next)} label={`${offer.name} quantity`} className="mb-4" data-id="element-628" data-mp="30a39q2m7t0l4" />
                    {(() => {
                  const ctaClass = `flex w-full scroll-mb-28 items-center justify-center gap-2 px-5 py-4 text-center font-display font-extrabold uppercase tracking-wide transition-[background-color,color,transform] duration-150 ease-grit active:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500 ${offer.featured ? 'bg-grit-500 text-ink-950 hover:bg-grit-400 text-2xl' : 'border-2 border-grit-500 text-grit-500 hover:bg-grit-500 hover:text-ink-950 text-xl'}`;
                  const ctaLabel = qty > 1 ? `Get ${orderUnits} X1 for ${money(orderTotal)}` : offer.cta;
                  const track = () => {
                    onSelectOffer(offer.id);
                    trackCustom(packEvents[offer.id].addToCart, {
                      quantity: qty,
                      value: orderTotal
                    });
                  };
                  return offer.variantId ? <a id={`pack-${offer.id}-cta`} href={checkoutUrl(offer.variantId, qty)} data-buy-intent="true" onClick={track} className={ctaClass} data-id="element-629" data-mp="10lalbim390ke">
                          {ctaLabel}
                        </a> : <button id={`pack-${offer.id}-cta`} type="button" onClick={track} className={ctaClass} data-id="element-630" data-mp="3ccxtazgdmzhw">
                          {ctaLabel}
                        </button>;
                })()}
                    <PromoCountdown variant="card" className="mt-3" data-id="element-631" data-mp="1i3956jk5vhfo" />
                    <UspsBadge className="mt-3" note={offer.note} data-id="element-632" data-mp="1i2zd8qfk307c" />
                    <SecureCheckoutNote className="mt-4" data-id="element-633" data-mp="1n1anh29rjcq3" />
                    {offer.badge && <p className="mt-2 text-xs font-bold uppercase tracking-[0.16em] text-grit-500" data-id="element-634" data-mp="191i0ni1cxgne">
                        {offer.badge}
                      </p>}
                    {offer.footnote && <p className="mt-3 text-[11px] leading-relaxed text-steel-500" data-id="element-635" data-mp="z6hmoccg16co">{offer.footnote}</p>}
                  </div>
                </article>
              </li>;
        })}
        </ul>

        <IncludedGear data-id="element-636" data-mp="2yh8z9dwbscv0" />

        <CompanyPack data-id="element-637" data-mp="2yhr5928si5g4" />

        <FreeUnitModal open={freeUnitOpen} onAccept={acceptFreeUnit} onDecline={declineFreeUnit} data-id="element-638" data-mp="2ygz7bkrpzvmo" />

        <div className="mt-10 flex flex-col gap-4 border-t border-white/10 pt-8 sm:flex-row sm:items-center sm:justify-between" data-id="element-639" data-mp="5uq42ym7g8ge">
          <div data-id="element-640" data-mp="aouclmb4cojl">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-641" data-mp="3t18xqldk593y">Secure checkout</p>
            <div className="mt-2" data-id="element-642" data-mp="wok8bgyg53v">
              <PaymentLogos data-id="element-643" data-mp="xzlo4mxh0rt" />
            </div>
          </div>
          <p className="max-w-md text-[13px] text-steel-400" data-id="element-644" data-mp="om95x24htggu">
            30-day returns with free return shipping · Lifetime warranty on multi-packs ·{' '}
            <Link to="/legal/shipping" className="font-semibold text-grit-500 hover:underline" data-id="element-645" data-mp="tjrgewy5s3qy">
              Shipping &amp; returns
            </Link>
          </p>
        </div>
      </div>
    </section>;
}